"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import { PortableText, toPlainText } from "@portabletext/react";
import { stegaClean } from "@sanity/client/stega";
import type { PortableTextBlock, PortableTextTextBlock } from "@sanity/types";
import { useReducedMotion } from "framer-motion";
import { Plus, Mic, ArrowUp } from "lucide-react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { cn } from "@/lib/utils";
import type { PageAiFeaturesSchema } from "@/sanity/pages/PageAiFeaturesTemplate";
import { SectionHeading } from "./ui";
import styles from "./ai-features.module.scss";
import motionStyles from "./ask-motion.module.scss";

type Phase = "typing" | "question" | "analysis" | "answer" | "complete";
type Playback = {
  phase: Phase;
  question: number;
  steps: number;
  answer: number;
};
const COMPLETE: Playback = {
  phase: "complete",
  question: Infinity,
  steps: Infinity,
  answer: Infinity,
};

function isTextBlock(block: PortableTextBlock): block is PortableTextTextBlock {
  return Array.isArray(block.children);
}

function revealAnswer(answer: PortableTextBlock[], count: number) {
  let remaining = count;
  return answer.map((block) => {
    if (!isTextBlock(block)) return block;
    return {
      ...block,
      children: block.children.flatMap((child) => {
        if (typeof child.text !== "string") return remaining > 0 ? [child] : [];
        const characters = Array.from(child.text);
        const text = characters.slice(0, remaining).join("");
        remaining = Math.max(0, remaining - characters.length);
        return [{ ...child, text }];
      }),
    };
  });
}

export default function AskSection({
  data,
}: {
  data?: PageAiFeaturesSchema["ask"];
}) {
  const [{ selected, previous, revision }, setConversation] = useState({
    selected: 0,
    previous: 1,
    revision: 0,
  });
  const [playback, setPlayback] = useState<Playback>(COMPLETE);
  const [visibility, setVisibility] = useState({ awake: false, auto: false });
  const [announcement, setAnnouncement] = useState("");
  const [answerOverflow, setAnswerOverflow] = useState(false);
  const chat = useRef<HTMLDivElement>(null);
  const thread = useRef<HTMLDivElement>(null);
  const options = useRef<HTMLFieldSetElement>(null);
  const busy = useRef(false);
  const elapsed = useRef(0);
  const lastFrame = useRef<number | null>(null);
  const asked = useRef(false);
  const restoreFocus = useRef<number | null>(null);
  const reducedMotion = useReducedMotion();
  const examples = useMemo(
    () =>
      data?.examples?.filter(
        (example) =>
          typeof example.question === "string" &&
          stegaClean(example.question).trim(),
      ),
    [data?.examples],
  );
  const activeIndex = examples?.[selected] ? selected : 0;
  const current = examples?.[selected] ?? examples?.[0];
  const animationQuestion = stegaClean(current?.question ?? "");
  const animationAnswer = useMemo(
    () => stegaClean(current?.answer ?? []),
    [current?.answer],
  );
  const earlier =
    examples?.[previous] && examples[previous] !== current
      ? examples[previous]
      : examples?.find((example) => example !== current);
  const isBusy = playback.phase !== "complete";

  useEffect(() => {
    if (current) return;
    busy.current = false;
    asked.current = false;
    elapsed.current = 0;
    lastFrame.current = null;
    restoreFocus.current = null;
    setPlayback(COMPLETE);
    setAnnouncement("");
  }, [current]);

  useLayoutEffect(() => {
    const exchange =
      thread.current?.querySelector<HTMLElement>("[data-ask-current]");
    if (exchange) exchange.scrollTop = 0;
  }, [revision, current?._key]);

  useLayoutEffect(() => {
    if (isBusy || restoreFocus.current === null) return;
    const index = restoreFocus.current;
    restoreFocus.current = null;
    if (
      document.activeElement === chat.current ||
      document.activeElement === document.body
    ) {
      options.current
        ?.querySelectorAll("button")
        [index]?.focus({ preventScroll: true });
    }
  }, [isBusy]);

  const ask = useCallback(
    (index: number) => {
      if (busy.current || !examples?.[index]) return;
      busy.current = true;
      asked.current = true;
      elapsed.current = 0;
      if (options.current?.contains(document.activeElement)) {
        restoreFocus.current = index;
        chat.current?.focus({ preventScroll: true });
      }
      setAnnouncement("");
      setConversation((state) => ({
        selected: index,
        previous: index === state.selected ? state.previous : state.selected,
        revision: state.revision + 1,
      }));
      setPlayback({ phase: "typing", question: 0, steps: 0, answer: 0 });
    },
    [examples],
  );

  useEffect(() => {
    const node = chat.current;
    if (!node) return;
    let inView = false;
    let autoVisible = false;
    const sync = () => {
      const visible = !document.hidden;
      lastFrame.current = null;
      setVisibility({ awake: inView && visible, auto: autoVisible && visible });
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        autoVisible = entry.isIntersecting && entry.intersectionRatio >= 0.45;
        sync();
      },
      { threshold: [0, 0.45] },
    );
    observer.observe(node);
    document.addEventListener("visibilitychange", sync);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
    };
  }, [data]);

  useEffect(() => {
    if (!visibility.auto || asked.current || reducedMotion || !current) return;
    const timer = window.setTimeout(() => ask(0), 500);
    return () => window.clearTimeout(timer);
  }, [visibility.auto, ask, reducedMotion, current]);

  useEffect(() => {
    const node = chat.current;
    if (!node || !current || !busy.current) return;
    const complete = () => {
      busy.current = false;
      setPlayback(COMPLETE);
      setAnnouncement(
        `${animationQuestion}. ${data?.coachName ?? "TS Coach"}: ${toPlainText(animationAnswer.filter(isTextBlock))}`,
      );
    };
    if (reducedMotion) {
      complete();
      return;
    }
    if (!visibility.awake) return;
    const questionLength = Array.from(animationQuestion).length;
    const answerLength = animationAnswer.reduce(
      (total, block) =>
        total +
        (isTextBlock(block)
          ? block.children.reduce(
              (length, child) =>
                length +
                (typeof child.text === "string"
                  ? Array.from(child.text).length
                  : 0),
              0,
            )
          : 0),
      0,
    );
    const steps = data?.steps?.length ?? 0;
    const submittedAt = (questionLength / 30) * 1000 + 250;
    const analysisAt = submittedAt + 350;
    const answerAt = analysisAt + steps * 450 + 400;
    const completedAt = answerAt + (answerLength / 70) * 1000 + 250;
    lastFrame.current = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      elapsed.current += now - (lastFrame.current ?? now);
      lastFrame.current = now;
      const time = elapsed.current;
      if (time >= completedAt) {
        complete();
        return;
      }
      const next: Playback = {
        phase:
          time < submittedAt
            ? "typing"
            : time < analysisAt
              ? "question"
              : time < answerAt
                ? "analysis"
                : "answer",
        question: Math.min(questionLength, Math.floor((time / 1000) * 30)),
        steps: Math.min(
          steps,
          Math.max(0, Math.floor((time - analysisAt) / 450)),
        ),
        answer: Math.min(
          answerLength,
          Math.max(0, Math.floor(((time - answerAt) / 1000) * 70)),
        ),
      };
      setPlayback((state) =>
        state.phase === next.phase &&
        state.question === next.question &&
        state.steps === next.steps &&
        state.answer === next.answer
          ? state
          : next,
      );
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [
    revision,
    current,
    animationQuestion,
    animationAnswer,
    data?.coachName,
    data?.steps,
    reducedMotion,
    visibility.awake,
  ]);

  useEffect(() => {
    const node = thread.current;
    if (!node) return;
    const sync = () => {
      const exchange = node.querySelector<HTMLElement>("[data-ask-current]");
      setAnswerOverflow(
        !!exchange && exchange.scrollHeight > exchange.clientHeight + 2,
      );
    };
    const observer = new ResizeObserver(sync);
    observer.observe(node);
    Array.from(node.children).forEach((child) => observer.observe(child));
    sync();
    return () => observer.disconnect();
  }, [current, earlier]);

  if (!data) return null;
  const answer = (example: typeof current, muted = false) => {
    if (!example) return null;
    const showCoach =
      muted ||
      playback.phase === "analysis" ||
      playback.phase === "answer" ||
      !isBusy;
    const showAnswer = muted || playback.phase === "answer" || !isBusy;
    const fullAnswer = example.answer ?? [];
    return (
      <div
        className={cn(styles.answer, motionStyles.coach)}
        data-ask-coach
        data-visible={showCoach}
      >
        <span className={styles.chatAvatar} data-ask-avatar>
          <Image src="/ai-features/fox-1.svg" width={36} height={36} alt="" />
        </span>
        <div className="min-w-0">
          <p className={styles.coachLabel} data-ask-label>
            {!muted && playback.phase === "analysis"
              ? "Foxing..."
              : data.coachName}
          </p>
          <ul className={styles.steps} data-ask-steps>
            {data.steps?.map((step, index) => (
              <li key={step} data-visible={muted || index < playback.steps}>
                {step}
              </li>
            ))}
          </ul>
          {fullAnswer.length ? (
            <div
              className={cn(styles.chatText, motionStyles.answer)}
              data-ask-answer
              data-visible={showAnswer}
              aria-hidden={!muted && isBusy ? true : undefined}
            >
              {!muted && isBusy ? (
                <>
                  <div className={motionStyles.answerSizer} aria-hidden="true">
                    <PortableText
                      value={fullAnswer}
                      components={richTextComponent}
                    />
                  </div>
                  <div className={motionStyles.answerStream} data-ask-stream>
                    <PortableText
                      value={revealAnswer(animationAnswer, playback.answer)}
                      components={richTextComponent}
                    />
                  </div>
                </>
              ) : (
                <PortableText
                  value={fullAnswer}
                  components={richTextComponent}
                />
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  };
  const question = (example: NonNullable<typeof current>, muted = false) => (
    <div
      className={motionStyles.prompt}
      data-visible={muted || playback.phase !== "typing"}
      data-ask-question
    >
      <span className={motionStyles.you}>You</span>
      <p className={styles.question}>{example.question}</p>
    </div>
  );
  return (
    <section id="ask" className={styles.ask}>
      <div className="px-[3rem]">
        <SectionHeading data={data} />
      </div>
      <div className={styles.chatBand}>
        <div
          id="askChat"
          ref={chat}
          tabIndex={-1}
          className={cn(styles.chat, motionStyles.chat)}
          role="group"
          aria-label={`Conversation with ${data.coachName ?? "TS Coach"}`}
          aria-busy={isBusy}
          data-ask-state={
            !isBusy ? "complete" : visibility.awake ? "playing" : "paused"
          }
          data-ask-phase={playback.phase}
          data-ask-busy={isBusy}
        >
          <div className={motionStyles.thread} ref={thread} data-ask-thread>
            {earlier && (
              <div
                className={cn(styles.earlier, motionStyles.earlier)}
                data-ask-earlier
              >
                {question(earlier, true)}
                {answer(earlier, true)}
              </div>
            )}
            {current && (
              <div
                className={styles.exchange}
                data-ask-current
                data-overflow={answerOverflow}
                tabIndex={answerOverflow && !isBusy ? 0 : undefined}
                aria-label={answerOverflow ? "Current conversation" : undefined}
                role={answerOverflow ? "group" : undefined}
                aria-hidden={isBusy ? true : undefined}
                ref={(node) => {
                  node?.toggleAttribute("inert", isBusy);
                }}
              >
                {question(current)}
                {answer(current)}
              </div>
            )}
          </div>
          <fieldset
            ref={options}
            className={cn(styles.suggestions, motionStyles.options)}
            disabled={isBusy}
            aria-hidden={isBusy ? true : undefined}
            data-ask-options
          >
            <legend>{data.repliesLabel}</legend>
            {examples?.map((example, i) => (
              <button
                key={example._key}
                type="button"
                aria-pressed={i === activeIndex}
                onClick={() => ask(i)}
                className={cn(
                  styles.suggestion,
                  i === activeIndex && styles.selectedSuggestion,
                )}
              >
                {example.question}
              </button>
            ))}
          </fieldset>
          <div className={styles.demoInput} aria-hidden="true">
            <Plus />
            <span
              data-ask-placeholder
              data-typing={playback.phase === "typing"}
            >
              {playback.phase === "typing" && current
                ? Array.from(animationQuestion)
                    .slice(0, playback.question)
                    .join("") || "\u00a0"
                : data.inputPlaceholder}
            </span>
            <Mic />
            <span className={styles.send}>
              <ArrowUp />
            </span>
          </div>
          <p className={styles.disclaimer}>{data.disclaimer}</p>
        </div>
        <p
          className="sr-only"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {announcement}
        </p>
      </div>
    </section>
  );
}
